// ContractsEditor.tsx
// Raid leaders attach contracts to a raid's open seats (md/CONTRACTS_PLAN.md):
// a headline, written terms started from a template, how many holders (or
// everyone the raid takes who matches), and optional role / spec / parse
// limits. Up to 3 active per raid. Editing makes a new version; players who
// already accepted keep the text they accepted.
// Server: auth/open_seats_routes.js (contracts section), rules in auth/contracts.js.
import React, { useEffect, useMemo, useState } from "react";
import { Icon, ROLE_COLOR, tc } from "./alignmentUi";
import SpecPicker, { PickerOption } from "./SpecPicker";

export type ContractHolder = { id: string; status: "pending_approval" | "live"; termsVersion: number; characterId: string; name: string; className: string | null };
export type RaidContract = {
  id: string; kind: string; headline: string; terms: string; version: number; status: "active" | "withdrawn" | "closed";
  seats: number | null; roleKeys: string[]; specIds: number[]; minParse: number | null;
  live: number; pending: number; seatsLeft: number | null; holders: ContractHolder[];
};
type Template = { kind: string; label: string; headline: string; terms: string };

const ERRORS: Record<string, string> = {
  invalid_headline: "Give it a headline of up to 80 characters.",
  invalid_terms: "Write the terms (up to 4000 characters).",
  unfilled_placeholder: "Fill in every [bracketed] part of the template first.",
  invalid_seats: "Holders must be between 1 and 40, or everyone who matches.",
  invalid_min_parse: "Minimum parse must be between 0 and 100.",
  too_many_contracts: "A raid can carry up to 3 contracts.",
  unknown_spec: "One of those specs is not part of this game.",
  unknown_role: "One of those roles is not part of this game.",
};

type Draft = { id?: string; kind: string; headline: string; terms: string; everyone: boolean; seats: number; roleKeys: string[]; specIds: number[]; minParse: string };
const emptyDraft = (t?: Template): Draft => ({
  kind: t?.kind || "custom", headline: t?.headline || "", terms: t?.terms || "",
  everyone: false, seats: 1, roleKeys: [], specIds: [], minParse: "",
});

export default function ContractsEditor({
  raidId, contracts, classes, specs, roles, onChanged,
}: {
  raidId: string;
  contracts: RaidContract[];
  classes: PickerOption[];
  specs: PickerOption[];
  roles: PickerOption[];
  onChanged: () => void;
}) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [footer, setFooter] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [showSpecs, setShowSpecs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch("/contract-templates", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setTemplates(d.templates || []); setFooter(d.footer || ""); } })
      .catch(() => {});
  }, []);

  const active = contracts.filter((c) => c.status === "active");
  const withdrawn = contracts.filter((c) => c.status !== "active" && c.holders.length > 0);
  const roleLabel = useMemo(() => new Map(roles.map((r) => [r.key, r.label])), [roles]);
  const specById = useMemo(() => new Map(specs.map((s) => [s.id, s])), [specs]);
  const kindLabel = (k: string) => templates.find((t) => t.kind === k)?.label || k;

  const edit = (c: RaidContract) => {
    setErr(null);
    setShowSpecs(c.specIds.length > 0);
    setDraft({
      id: c.id, kind: c.kind, headline: c.headline, terms: c.terms,
      everyone: c.seats === null, seats: c.seats || 1, roleKeys: c.roleKeys, specIds: c.specIds,
      minParse: c.minParse === null ? "" : String(c.minParse),
    });
  };

  const save = async () => {
    if (!draft) return;
    setBusy(true); setErr(null);
    try {
      const body = {
        kind: draft.kind, headline: draft.headline, terms: draft.terms,
        seats: draft.everyone ? null : draft.seats, roleKeys: draft.roleKeys, specIds: draft.specIds,
        minParse: draft.minParse.trim() === "" ? null : Number(draft.minParse),
      };
      const r = await fetch(draft.id ? `/raids/${raidId}/contracts/${draft.id}` : `/raids/${raidId}/contracts`, {
        method: draft.id ? "PUT" : "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(ERRORS[j.error] || j.error || "Something went wrong");
      setDraft(null);
      onChanged();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (id: string) => {
    setBusy(true); setErr(null);
    await fetch(`/raids/${raidId}/contracts/${id}/withdraw`, { method: "POST", credentials: "include" }).catch(() => null);
    setBusy(false);
    onChanged();
  };

  const limits = (c: { roleKeys: string[]; specIds: number[]; minParse: number | null }) => [
    c.roleKeys.length ? c.roleKeys.map((k) => roleLabel.get(k) || k).join(" / ") : null,
    c.specIds.length ? `${c.specIds.length} spec${c.specIds.length === 1 ? "" : "s"}` : null,
    c.minParse !== null ? `parse ${c.minParse}+` : null,
  ].filter(Boolean).join(" · ");

  const holdersOf = (c: RaidContract) => (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {c.holders.map((h) => (
        <span key={h.id} title={`Accepted version ${h.termsVersion}`}
          className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${h.status === "live" ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300" : "bg-amber-500/15 text-amber-800 dark:text-amber-200"}`}>
          {h.name}{h.status === "live" ? "" : " · waiting approval"}{h.termsVersion !== c.version ? ` · v${h.termsVersion}` : ""}
        </span>
      ))}
    </div>
  );

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">Contracts</span>
        <span className="text-[11px] text-[var(--c-ink-4)]">{active.length} of 3 · terms freeze when a player accepts</span>
      </div>

      <div className="space-y-2">
        {active.map((c) => (
          <div key={c.id} className="rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-3">
            <div className="flex flex-wrap items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-bold text-[var(--c-ink)]">📜 {c.headline}</div>
                <div className="text-[12px] text-[var(--c-ink-3)]">
                  {kindLabel(c.kind)} · {c.seats === null ? "everyone who matches" : `${c.live} of ${c.seats} holders`}
                  {limits(c) ? ` · ${limits(c)}` : ""} · v{c.version}
                  {c.pending > 0 ? ` · ${c.pending} waiting approval` : ""}
                </div>
              </div>
              <button type="button" disabled={busy} onClick={() => edit(c)} className="rounded-lg border border-[var(--c-line)] px-2.5 py-1 text-[12px] font-semibold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)]">Edit</button>
              <button type="button" disabled={busy} onClick={() => withdraw(c.id)} title="No new takers. People who already accepted keep their contract."
                className="rounded-lg border border-[var(--c-line)] px-2.5 py-1 text-[12px] font-semibold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)]">Withdraw</button>
            </div>
            {c.holders.length > 0 && holdersOf(c)}
          </div>
        ))}
        {withdrawn.map((c) => (
          <div key={c.id} className="rounded-xl border border-dashed border-[var(--c-line)] p-3 opacity-75">
            <div className="text-[13px] font-semibold text-[var(--c-ink-2)]">📜 {c.headline} <span className="font-normal text-[var(--c-ink-4)]">· withdrawn, still binding for those who accepted</span></div>
            {holdersOf(c)}
          </div>
        ))}
      </div>

      {!draft && active.length < 3 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {templates.map((t) => (
            <button key={t.kind} type="button" onClick={() => { setErr(null); setShowSpecs(false); setDraft(emptyDraft(t)); }}
              className="rounded-lg border border-dashed border-[var(--c-line-strong)] px-2.5 py-1 text-[12px] font-semibold text-[var(--c-ink-2)] hover:bg-[var(--c-sunken)]">
              + {t.label}
            </button>
          ))}
        </div>
      )}

      {draft && (
        <div className="mt-3 space-y-3 rounded-2xl border border-[var(--c-accent)] bg-[var(--c-sunken)] p-4">
          <div className="text-[12px] font-bold text-[var(--c-ink-2)]">{draft.id ? "Edit contract" : `New contract · ${kindLabel(draft.kind)}`}{draft.id && <span className="font-normal text-[var(--c-ink-4)]"> · saving makes a new version; accepted terms stay as they were</span>}</div>
          <label className="block text-[12px] font-semibold text-[var(--c-ink-2)]">
            Headline <span className="font-normal text-[var(--c-ink-4)]">(shown on the seat and in Discord)</span>
            <input value={draft.headline} maxLength={80} onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
              className="mt-1 block w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2 text-[14px] text-[var(--c-ink)] outline-none focus:border-[var(--c-accent)]" />
          </label>
          <label className="block text-[12px] font-semibold text-[var(--c-ink-2)]">
            Terms <span className="font-normal text-[var(--c-ink-4)]">(replace every [bracket])</span>
            <textarea value={draft.terms} maxLength={4000} rows={4} onChange={(e) => setDraft({ ...draft, terms: e.target.value })}
              className="mt-1 block w-full rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-3 py-2 text-[14px] text-[var(--c-ink)] outline-none focus:border-[var(--c-accent)]" />
          </label>
          {footer && <p className="text-[11px] italic text-[var(--c-ink-4)]">Always added: {footer}</p>}

          <div className="flex flex-wrap items-end gap-4">
            <div className="text-[12px] font-semibold text-[var(--c-ink-2)]">
              Holders
              <div className="mt-1 flex items-center gap-2">
                <label className="flex items-center gap-1.5 font-normal"><input type="radio" checked={draft.everyone} onChange={() => setDraft({ ...draft, everyone: true })} className="accent-amber-500" />Everyone who matches</label>
                <label className="flex items-center gap-1.5 font-normal"><input type="radio" checked={!draft.everyone} onChange={() => setDraft({ ...draft, everyone: false })} className="accent-amber-500" />Up to</label>
                <input type="number" min={1} max={40} value={draft.seats} disabled={draft.everyone}
                  onChange={(e) => setDraft({ ...draft, seats: Math.max(1, Math.min(40, Number(e.target.value) || 1)) })}
                  className="w-16 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1 text-[14px] text-[var(--c-ink)] disabled:opacity-40" />
              </div>
            </div>
            <label className="text-[12px] font-semibold text-[var(--c-ink-2)]">
              Minimum parse <span className="font-normal text-[var(--c-ink-4)]">(optional)</span>
              <input type="number" min={0} max={100} placeholder="Any" value={draft.minParse} onChange={(e) => setDraft({ ...draft, minParse: e.target.value })}
                className="mt-1 block w-24 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] px-2 py-1 text-[14px] text-[var(--c-ink)]" />
            </label>
          </div>

          <div>
            <div className="mb-1 text-[12px] font-semibold text-[var(--c-ink-2)]">Roles <span className="font-normal text-[var(--c-ink-4)]">(none picked = any role)</span></div>
            <div className="flex flex-wrap gap-1.5">
              {roles.map((r) => {
                const on = draft.roleKeys.includes(r.key);
                const color = ROLE_COLOR[r.key] || "#94a3b8";
                return (
                  <button key={r.key} type="button" aria-pressed={on}
                    onClick={() => setDraft({ ...draft, roleKeys: on ? draft.roleKeys.filter((k) => k !== r.key) : [...draft.roleKeys, r.key] })}
                    className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${on ? "" : "border-[var(--c-line)] text-[var(--c-ink-3)]"}`}
                    style={on ? { color: tc(color), background: `${color}24`, borderColor: `${color}80` } : undefined}>
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <button type="button" onClick={() => setShowSpecs((v) => !v)} className="text-[12px] font-semibold text-[var(--c-accent-ink)] hover:underline">
              {showSpecs ? "Hide specs" : draft.specIds.length ? `Specs: ${draft.specIds.length} picked` : "Limit to specific specs"}
            </button>
            {draft.specIds.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {draft.specIds.map((id) => { const s = specById.get(id); return s ? <Icon key={id} src={s.image_url} alt={s.label} size={22} /> : null; })}
              </div>
            )}
            {showSpecs && (
              <SpecPicker className="mt-2" classes={classes} specs={specs} selected={draft.specIds}
                onToggle={(id) => setDraft({ ...draft, specIds: draft.specIds.includes(id) ? draft.specIds.filter((x) => x !== id) : [...draft.specIds, id] })} />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={busy} onClick={save}
              className="rounded-xl bg-[var(--c-accent)] px-4 py-2 text-[13px] font-bold text-[var(--c-ink-on-fill)] hover:bg-[var(--c-accent-hover)] disabled:opacity-50 dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500">
              {draft.id ? "Save new version" : "Add contract"}
            </button>
            <button type="button" onClick={() => setDraft(null)} className="rounded-xl border border-[var(--c-line)] px-4 py-2 text-[13px] font-semibold text-[var(--c-ink-2)]">Cancel</button>
          </div>
        </div>
      )}
      {err && <p className="mt-2 text-[12px] text-[var(--c-bad-ink)]">{err}</p>}
    </div>
  );
}
